import { Component, Injector, ChangeDetectionStrategy, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subscription, forkJoin, of, switchMap, take } from 'rxjs';
import { ProfileService } from '../../../common/service/data/profile.service';
import { MeterMediaType } from '../../../consumption/model/const/enums';
import { PriceList } from '../../../consumption/model/pricelist';
import { PriceListService } from '../../../consumption/service/pricelist.service';
import { AutoCompleteModel } from '../../../core/component/autocomplete/autocomplete-model';
import { FormComponentBase } from '../../../core/component/forms/form-component.base';
import { SelectionItem } from '../../../core/component/select/select.component';
import { ColumnDefinition, ColumnVisibility } from '../../../core/model/column-definition';
import { Command, CommandBase, FilteredCommand, standardCommand } from '../../../core/model/command';
import { MenuItemDefinition } from '../../../core/model/menu-item-definition';
import { ActionWindowService } from '../../../core/service/action-window.service';
import { DataService } from '../../../core/service/base/data.service';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { DeleteFormCommandHandler } from '../../../core/service/command/delete-form-command-handler.service';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NativeWindowService } from '../../../core/service/native-window.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { NonResidentialProperty } from '../../../overview/model/non-residential-property';
import { ResidentialProperty } from '../../../overview/model/residential-property';
import { filterExecutorOptions, isExecutorUserInGroup } from '../../../round-planning/model/const/executor-validator';
import { propertyCommand } from '../../command/property.command';
import { Area } from '../../model/area';
import { Building } from '../../model/building';
import { Company } from '../../model/company';
import { AreaType, AreaTypeToString, CustomPropertyDataType, RentalObjectCategory } from '../../model/const/enums';
import { LinkedCustomProperty } from '../../model/linked-custom-property';
import { Property } from '../../model/property';
import { PropertyTax } from '../../model/property-tax';
import { PropertyAppendix } from '../../model/property-appendix';
import { CompanyService } from '../../service/data/company.service';
import { DwellingService } from '../../service/data/dwelling.service';
import { OtherRentalObjectService } from '../../service/data/other-rental-object.service';
import { ParkingService } from '../../service/data/parking.service';
import { PremisesService } from '../../service/data/premises.service';
import { PropertyDocumentService } from '../../service/data/property-document-service';
import { PropertyService } from '../../service/data/property.service';
import { PropertyHelpTextService } from '../../service/helpText/property-help-text.service';
import { ChangePropertyDocumentVisibilityComponent } from '../change-property-document-visibility/change-property-document-visibility.component';
import { PropertyDocumentFormComponent } from '../property-contact-form/property-document-form.component';
import { Features, ModuleNames } from '../../../common/model/account-definition';
import { UserAccountService } from '../../../common/service/user-account.service';
import { PropertyFormCommandHandler } from '../../service/command/property-form-command-handler.service';
import { DimensionLabelsService } from '../../../accounting/service/data/dimension-labels.service';
import { DimensionLabels } from '../../../accounting/model/dimension-labels';
import { LanguageService } from '../../../core/service/language.service';
import { PropertyErrandSubCategoryResponsibilityComponent } from '../property-errand-subcategory-responsibility/property-errand-subcategory-responsibility.component';

@Component({
  selector: 'app-property-form',
  templateUrl: './property-form.component.html',
  styleUrls: ['./property-form.component.scss'],
  providers: [
    DeleteFormCommandHandler,
    PropertyFormCommandHandler,
    PropertyHelpTextService,
    { provide: DataService, useExisting: PropertyService },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PropertyFormComponent extends FormComponentBase<Property> {
  @ViewChild(PropertyErrandSubCategoryResponsibilityComponent)
  private errandSubCategoryResponsibilities?: PropertyErrandSubCategoryResponsibilityComponent;

  constructor(
    private _cf: FormControlExFactory,
    private _companyService: CompanyService,
    private _priceListService: PriceListService,
    private _fb: FormBuilder,
    private _helpTextService: PropertyHelpTextService,
    private _profileService: ProfileService,
    private dwellingService: DwellingService,
    private otherRenalObjectService: OtherRentalObjectService,
    private parkingService: ParkingService,
    private premisesService: PremisesService,
    private _dataService: PropertyService,
    private _cdf: ColumnDefinitionFactory,
    private documentService: PropertyDocumentService,
    private _actionWindowService: ActionWindowService,
    private _injector: Injector,
    private _nativeWindow: NativeWindowService,
    private _userAccountService: UserAccountService,
    private readonly _dimensionLabelsService: DimensionLabelsService,
    private readonly _languageService: LanguageService,
    activatedRoute: ActivatedRoute,
    dispatcher: CommandDispatcher,
    handlerResolver: PropertyFormCommandHandler,
    messageService: MessageService,
    navigation: NavigationService
  ) {
    super(activatedRoute, _dataService, messageService, navigation, dispatcher, handlerResolver);
    this.addMenuItemDefinition('CREATE_BTN', standardCommand.create);

    this.buildingColumnDefinition = [
      this._cdf.create('COMMON.NUMBER', 'number'),
      this._cdf.create('BUILDING_LIST.NAME_COLUMN', 'name'),
      this._cdf.create('BUILDING_LIST.FLOORS_IN_BUILDING_COLUMN', 'floorsInBuilding'),
      this._cdf.create('BUILDING_LIST.BUILD_YEAR_COLUMN', 'buildYear'),
      this._cdf.create('BUILDING_LIST.RECONSTRUCTION_YEAR_COLUMN', 'reconstructionYear'),
      this._cdf.create('COMMON.NOTE', 'firstPartOfNote'),
      this._cdf.create('COMMON.COMPANY_NUMBER', 'companyNumber', ColumnVisibility.optional),
      this._cdf.create('COMMON.COMPANY', 'companyName', ColumnVisibility.optional),
    ];

    for (let i = 1; i < 100; i++) {
      this.orderSelection.push(new SelectionItem(i.toString(), i));
    }

    this.documentsColumnDefinition = [
      this._cdf.create('DOCUMENT.CAPTION', 'caption'),
      this._cdf.createDateTime('DOCUMENT.UPLOADED_DATE', 'uploadedDate'),
      this._cdf.create('DOCUMENT.SIZE', 'size', ColumnVisibility.optional),
      this._cdf.createTranslation('DOCUMENT.DOCUMENT_TYPE', 'connectionTypeText'),
      this._cdf.create('COMMON.DISPLAYED_FOR', 'visibilityText'),
    ];
    this.documentsContextMenuDefinitions = [
      new MenuItemDefinition('DOWNLOAD_BTN', propertyCommand.downloadDocument),
      new MenuItemDefinition('COMMON.CHANGE_DISPLAYED_FOR', propertyCommand.changeVisibility),
      new MenuItemDefinition('DELETE_BTN', propertyCommand.deleteDocument),
    ];

    this.AddSubscription(
      _userAccountService.current.subscribe({
        next: account => {
          if (account.hasAccess(ModuleNames.Properties, Features.EditProperty)) {
            if (!this.menuDefinition.find(item => item.commandId === propertyCommand.moveToCompany)) {
              this.menuDefinition.push(
                new MenuItemDefinition('PROPERTY.MOVE_TO_COMPANY', propertyCommand.moveToCompany, null, null, this.contextId)
              );
            }
            this.addMenuItemDefinition('PROPERTY_TAX.MENU_ITEM_TITLE', propertyCommand.propertyTaxRental);
          }
          if (
            account.hasAccess(ModuleNames.Properties, Features.LinkPropertyBaseDocuments) ||
            account.hasAccess(ModuleNames.Properties, Features.EditCompany)
          ) {
            this.allowLinkPropertyDocument = true;
          }
          this.allowErrandSubCategoryResponsibilities = account.hasAccess(ModuleNames.Errands, Features.ManageErrandTags);
        },
      })
    );
  }

  allowLinkPropertyDocument: boolean;
  allowErrandSubCategoryResponsibilities = false;

  documentsColumnDefinition: ColumnDefinition[];
  documentsContextMenuDefinitions: MenuItemDefinition[];

  orderSelection: SelectionItem[] = [];

  dwelling: ResidentialProperty = new ResidentialProperty({ category: RentalObjectCategory.Dwelling });
  dwellingExist: boolean;
  otherRentalObject: NonResidentialProperty = new NonResidentialProperty({ category: RentalObjectCategory.Other });
  otherRentalObjectExist: boolean;
  parking: NonResidentialProperty = new NonResidentialProperty({ category: RentalObjectCategory.Parking });
  parkingExist: boolean;
  premises: ResidentialProperty = new ResidentialProperty({ category: RentalObjectCategory.Premises });
  premisesExist: boolean;
  faultNotificationRecipientSelection: SelectionItem[];
  faultNotificationExecutorGroupSelection: SelectionItem[];
  companyBankAccountSelection: SelectionItem[];
  _companies: Company[];
  companies: any[];
  buildingColumnDefinition: ColumnDefinition[];
  homeQOfficeName: string = '';
  isHomeQUsed: boolean;
  customProperties: {
    form: FormGroup;
    selection: SelectionItem[];
    showInt: boolean;
    showText: boolean;
    showDate: boolean;
    showBoolean: boolean;
    showSelection: boolean;
    showNumeric: boolean;
  }[] = [];

  consumptionIsExpanded = false;
  consumptionExpanded(expanded: boolean): void {
    this.consumptionIsExpanded = true;
  }

  get areas(): FormArray {
    return this.form.controls['areas'] as FormArray;
  }

  private _createAreaGroup(model: Area): FormGroup {
    return this._fb.group({
      id: this._cf.createHidden(model.id),
      type: this._cf.createMandatory('AREA.AREA_TYPE', model.type),
      value: this._cf.createMandatory('AREA.VALUE', model.value),
      measuredDate: this._cf.create('AREA.MEASURED_DATE', model.measuredDate),
      measuredBy: this._cf.create('AREA.MEASURED_BY', model.measuredBy),
    });
  }

  addArea() {
    this.areas.push(this._createAreaGroup(new Area()));
  }

  removeArea(index: number) {
    this.areas.removeAt(index);
    this.form.markAsDirty();
  }

  areaTypes: SelectionItem[] = [
    new SelectionItem(AreaTypeToString(AreaType.BOA), AreaType.BOA),
    new SelectionItem(AreaTypeToString(AreaType.LOA), AreaType.LOA),
    new SelectionItem(AreaTypeToString(AreaType.BRA), AreaType.BRA),
    new SelectionItem(AreaTypeToString(AreaType.BIA), AreaType.BIA),
    new SelectionItem(AreaTypeToString(AreaType.BTA), AreaType.BTA),
    new SelectionItem(AreaTypeToString(AreaType.OVA), AreaType.OVA),
    new SelectionItem(AreaTypeToString(AreaType.AGAR), AreaType.AGAR),
    new SelectionItem(AreaTypeToString(AreaType.ATEMP), AreaType.ATEMP),
    new SelectionItem(AreaTypeToString(AreaType.NTA), AreaType.NTA),
    new SelectionItem(AreaTypeToString(AreaType.LA), AreaType.LA),
  ];

  protected getFormGroup(): FormGroup {
    var formGroup = this._fb.group({
      company: this.dataContext.isNew
        ? this._cf.createMandatory('PROPERTY.COMPANY', this.dataContext.company)
        : this._cf.createReadonly('PROPERTY.COMPANY', this.dataContext.company),
      costPlace: this._cf.createMaxLength(
        'PROPERTY.COST_PLACE',
        this.dataContext.costPlace,
        10,
        this.getPropertyCostPlaceHelpText(this._dimensionLabelsService.defaults)
      ),
      costBearer: this._cf.createMaxLength(
        'PROPERTY.COST_BEARER',
        this.dataContext.costBearer,
        10,
        this.getPropertyCostBearerHelpText(this._dimensionLabelsService.defaults)
      ),
      note: this._cf.createMaxLength('COMMON.NOTE', this.dataContext.note, 3000),
      companyBankAccountId: this._cf.create('COMPANY.DEFAULT_INVOICE_ACCOUNT', this.dataContext.companyBankAccountId),
      paymentReceiver: this._cf.createMaxLength('COMPANY.PAYMENT_RECIEVER', this.dataContext.paymentReceiver, 50),
      municipality: this._cf.createWithOptions('PROPERTY.MUNICIPALITY', this.dataContext.municipality, { length: 50, required: true }),
      propertyName: this._cf.createWithOptions('PROPERTY.NAME', this.dataContext.propertyName, { length: 100, required: true }),
      propertyNumber: this._cf.createWithOptions('PROPERTY.NUMBER', this.dataContext.propertyNumber, { length: 30, required: true }),
      faultNotificationRecipient: this._cf.create('FAULT_NOTIFICATION.RESOURCE', null),
      faultNotificationExecutorWebUser: this._cf.create('ERRANDLIST_WEBUSERS', null),
      faultNotificationExecutorGroup: this._cf.create('ERRANDLIST_EXECUTORGROUPS', null),
      dwellingInspectionExecutorWebUser: this._cf.create('ERRANDLIST_WEBUSERS', null),
      specialTermsDwelling: this._cf.createMaxLength('COMPANY.SPECIAL_TERMS_DWELLING', this.dataContext.specialTermsDwelling, 3000),
      specialTermsParking: this._cf.createMaxLength('COMPANY.SPECIAL_TERMS_PARKING', this.dataContext.specialTermsParking, 3000),
      generalTermsDwelling: this._cf.createMaxLength('COMPANY.GENERAL_TERMS_DWELLING', this.dataContext.generalTermsDwelling, 4000),
      generalTermsParking: this._cf.createMaxLength('COMPANY.GENERAL_TERMS_PARKING', this.dataContext.generalTermsParking, 4000),
      rentPaymentTermsDwelling: this._cf.createMaxLength(
        'COMPANY.RENT_PAYMENT_TERMS_DWELLING',
        this.dataContext.rentPaymentTermsDwelling,
        1000
      ),
      rentPaymentTermsParking: this._cf.createMaxLength(
        'COMPANY.RENT_PAYMENT_TERMS_PARKING',
        this.dataContext.rentPaymentTermsParking,
        1000
      ),
      imdPriceListColdWaterId: this._cf.create('CONSUMPTION.PRICELIST.PRICELIST_COLD_WATER', this.dataContext.imdPriceListColdWaterId),
      imdPriceListElectricityId: this._cf.create('CONSUMPTION.PRICELIST.PRICELIST_ELECTRICITY', this.dataContext.imdPriceListElectricityId),
      imdPriceListChargingElectricityId: this._cf.create('CONSUMPTION.PRICELIST.PRICELIST_CHARGING_ELECTRICITY', this.dataContext.imdPriceListChargingElectricityId),
      imdPriceListHotWaterId: this._cf.create('CONSUMPTION.PRICELIST.PRICELIST_HOT_WATER', this.dataContext.imdPriceListHotWaterId),
      imdPriceListHeatingId: this._cf.create('CONSUMPTION.PRICELIST.PRICELIST_HEATING', this.dataContext.imdPriceListHeatingId),
      appendices: this._fb.array(this.dataContext.appendices.map(item => this._createAppendixGroup(item))),
      //hasNegotiationProcedure: this._cf.create('PROPERTY.HAS_NEGOTIATION_PROCEDURE', this.dataContext.hasNegotiationProcedure),
      areas: this._fb.array(this.dataContext.areas.map(item => this._createAreaGroup(item))),
      propertyCustomProperties: this._createCustomPropertiesForm(this.dataContext.propertyCustomProperties),
    });
    return formGroup;
  }

  canExecute(command: CommandBase): boolean {
    switch (command.id) {
      case propertyCommand.deleteDocument:
      case propertyCommand.downloadDocument:
      case propertyCommand.changeVisibility:
        return command instanceof Command && command.context;
      case propertyCommand.propertyTaxRental:
        return !!this.dataContext?.id;
      default:
        return super.canExecute(command);
    }
  }
  private changeVisibilitySub: Subscription;

  execute(command: CommandBase): void {
    if (command instanceof Command) {
      switch (command.id) {
        case propertyCommand.propertyTaxRental: {
          const propertyId = this.dataContext?.id;
          if (propertyId) {
            const totalAffectedItemsControlValue = [propertyId].length;
            const includedIds = [propertyId];
            const excludedIds: string[] = [];
            super.execute(new FilteredCommand(propertyCommand.propertyTaxRental, excludedIds, includedIds, totalAffectedItemsControlValue));
          }
          return;
        }
        case propertyCommand.deleteDocument:
          this.AddSubscription(
            this.documentService
              .delete(command.context.id)
              .pipe(switchMap(() => this.documentService.getByOwnerId(this.dataContext.id)))
              .subscribe(res => (this.dataContext.documents = res))
          );
          return;
        case propertyCommand.downloadDocument:
          this.AddSubscription(
            this.documentService.download((<Command>command).context).subscribe(res => {
              this._nativeWindow.save(res);
            })
          );
          return;

        case propertyCommand.changeVisibility:
          if (this.changeVisibilitySub) {
            this.changeVisibilitySub.unsubscribe();
          }
          this.changeVisibilitySub = this._actionWindowService
            .open(ChangePropertyDocumentVisibilityComponent, this._injector, null, { linkedPropertyDocuments: [command.context] })
            .subscribe(result => {
              Object.assign(command.context, result);
              this.changeVisibilitySub.unsubscribe();
            });
          break;
      }
    }
    super.execute(command);
  }

  protected getHelpTextDictionary() {
    return this._helpTextService.getDictionary();
  }

  protected createModel(): Property {
    return new Property();
  }

  get rentalObjectsExist(): boolean {
    return this.dwellingExist || this.parkingExist || this.otherRentalObjectExist || this.premisesExist;
  }

  onBuildingRowClicked(e: { context: Building; event: any }): void {
    this.navigation.navigate(['my-property', 'building', 'edit', e.context.id], null, e.event.ctrlKey);
  }

  appendixTitles: SelectionItem[] = [];

  get appendices(): FormArray {
    return this.form.controls['appendices'] as FormArray;
  }

  addAppendix() {
    this.appendices.push(this._createAppendixGroup(new PropertyAppendix()));
  }

  removeAppendix(index: number) {
    this.appendices.removeAt(index);
    this.form.markAsDirty();
  }

  private _createAppendixGroup(model: PropertyAppendix): FormGroup {
    return this._fb.group({
      id: this._cf.createHidden(model.id),
      property: this._cf.createHidden(model.property),
      appendix: this._cf.createMandatory('APPENDIX.APPENDIX_DWELLING', model.appendix),
      sortOrder: this._cf.createMandatory('APPENDIX.SORTORDER', model.sortOrder),
    });
  }

  private _createCustomPropertiesForm(propertyCustomProperties: LinkedCustomProperty[]): FormArray {
    const formPropertiesArray = this._fb.array<FormGroup>([]);
    this.customProperties = [];
    for (let property of propertyCustomProperties ?? []) {
      const form = this._fb.group({
        id: this._cf.createHidden(property.id),
        customPropertyId: this._cf.createHidden(property.customPropertyId),
        numericValue: this._cf.create(property.caption, property.numericValue),
        textValue: this._cf.create(property.caption, property.textValue),
        dateValue: this._cf.create(property.caption, property.dateValue),
        boolValue: this._cf.create(property.caption, property.boolValue),
        dataType: this._cf.create(property.caption, property.dataType),
      });
      formPropertiesArray.push(form);

      this.customProperties.push({
        form: form,
        selection: (property.selectableValues ?? []).map(x => new SelectionItem(x, x)),
        showInt: property.dataType === CustomPropertyDataType.Numeric && property.decimalCount === 0,
        showText: property.dataType === CustomPropertyDataType.Text,
        showDate: property.dataType === CustomPropertyDataType.Date,
        showBoolean: property.dataType === CustomPropertyDataType.Boolean,
        showSelection: property.dataType === CustomPropertyDataType.Selection,
        showNumeric: property.dataType === CustomPropertyDataType.Numeric && property.decimalCount !== 0,
      });
    }

    return formPropertiesArray;
  }

  private _imdPriceLists: PriceList[];
  imdElectricitySelection: SelectionItem[];
  imdChargingElectricitySelection: SelectionItem[];
  imdHotWaterSelection: SelectionItem[];
  imdColdWaterSelection: SelectionItem[];
  imdHeatingSelection: SelectionItem[];

  private webUserExecutorGroups: Map<string, string[]> = new Map<string, string[]>();
  executorGroups: AutoCompleteModel[];
  executorWebUsers: AutoCompleteModel[];
  faultNotificationExecutorWebUsers: AutoCompleteModel[];
  inspectorWebUsers: AutoCompleteModel[];
  pendingPropertyTaxes: PropertyTax[] = [];
  pendingRemovedPropertyTaxIds: string[] = [];
  pendingRemovedBuildingPropertyTaxAssessmentIds: string[] = [];

  protected init(data: Property) {
    this.AddSubscription(this._dimensionLabelsService.getDefaultLabels().subscribe(labels => this.applyDimensionLabels(labels)));

    this._profileService.getConfig().subscribe(res => {
      this.isHomeQUsed = res.useHomeQ;
      if (res.useHomeQ) {
        this.AddSubscription(this._dataService.getHomeQCompanyName(data.id).subscribe(res => (this.homeQOfficeName = res)));
      }
    });
    this.AddSubscription(
      this._dataService.getFaultNotificationRecipients().subscribe(res => {
        this.faultNotificationRecipientSelection = res;
      })
    );
    this.AddSubscription(
      this._dataService.getCompanyBankAccounts(data.id).subscribe(res => {
        this.companyBankAccountSelection = res;
      })
    );
    super.init(data);

    if (data.company) {
      this.AddSubscription(this._dimensionLabelsService.getForCompany(data.company).subscribe(labels => this.applyDimensionLabels(labels)));
    }

    this.AddSubscription(
      this.form.controls['company'].valueChanges.subscribe(companyId => {
        this._dimensionLabelsService.getForCompany(companyId).subscribe(labels => this.applyDimensionLabels(labels));
      })
    );

    this.AddSubscription(
      this.form.controls['company'].valueChanges.subscribe(val => {
        this.updatePriceListSelection();
      })
    );
    this.AddSubscription(
      this._dataService.getAppendixTitels().subscribe(res => {
        this.appendixTitles = res;
      })
    );
    this.AddSubscription(
      this._priceListService.getList().subscribe(result => {
        this._imdPriceLists = result;
        this.updatePriceListSelection();
      })
    );
    this._companyService.getList().subscribe(data => {
      this._companies = data;
      this.updatePriceListSelection();
      this.companies = data.map((item: Company) => {
        return { name: item.toString(), value: item.id };
      });
      if (this.companies.length == 1) {
        this.form.controls['company'].setValue(this.companies[0].value);
      }
    });

    this.AddSubscription(
      this._dataService.getDefaults().subscribe(defaults => {
        this.executorGroups = defaults.executorGroups.map(x => new AutoCompleteModel(x.name, x.id));
        this.executorWebUsers = defaults.executorWebUsers.map(x => new AutoCompleteModel(x.personName || x.name, x.id));
        this.faultNotificationExecutorWebUsers = this.executorWebUsers.slice();
        this.inspectorWebUsers = defaults.inspectorWebUsers.map(x => new AutoCompleteModel(x.personName || x.name, x.id));

        const assignedFNWebUser = data?.faultNotificationExecutorWebUser;
        if (assignedFNWebUser?.id && !this.executorWebUsers.some(x => x.id === assignedFNWebUser.id)) {
          // if assigned user is not in the pool of available users, add them to the drop-down.
          this.executorWebUsers.push(new AutoCompleteModel(assignedFNWebUser.personName || assignedFNWebUser.name, assignedFNWebUser.id));
        }
        const assignedResourceWebUser = data?.faultNotificationRecipient;
        if (assignedResourceWebUser?.id && !this.executorWebUsers.some(x => x.id === assignedResourceWebUser.id)) {
          // if assigned user is not in the pool of available users, add them to the drop-down.
          this.executorWebUsers.push(
            new AutoCompleteModel(assignedResourceWebUser.personName || assignedResourceWebUser.name, assignedResourceWebUser.id)
          );
        }

        const assignedInspectorWebUser = data?.dwellingInspectionExecutorWebUser;
        if (assignedInspectorWebUser?.id && !this.inspectorWebUsers.some(x => x.id === assignedInspectorWebUser.id)) {
          // if assigned user is not in the pool of available users, add them to the drop-down.
          this.inspectorWebUsers.push(
            new AutoCompleteModel(assignedInspectorWebUser.personName || assignedInspectorWebUser.name, assignedInspectorWebUser.id)
          );
        }

        this.webUserExecutorGroups.clear();
        defaults.executorWebUsers.forEach(user => this.webUserExecutorGroups.set(user.id, user.executorGroups));
        if (assignedFNWebUser?.id) {
          this.webUserExecutorGroups.set(assignedFNWebUser.id, assignedFNWebUser.executorGroups ?? []);
        }

        this.form.controls['faultNotificationRecipient'].setValue(
          this.executorWebUsers.find(x => x.id === this.dataContext.faultNotificationRecipientId) || null
        );
        this.form.controls['faultNotificationExecutorWebUser'].setValue(
          this.executorWebUsers.find(x => x.id === this.dataContext.faultNotificationExecutorWebUserId) || null,
          { emitEvent: false }
        );
        this.form.controls['dwellingInspectionExecutorWebUser'].setValue(
          this.inspectorWebUsers.find(x => x.id === this.dataContext.dwellingInspectionExecutorWebUserId) || null
        );
        this.form.controls['faultNotificationExecutorGroup'].setValue(
          this.executorGroups.find(x => x.id === this.dataContext.faultNotificationExecutorGroupId) || null,
          { emitEvent: false }
        );

        this.syncFaultNotificationExecutorWebUsers();
      })
    );

    this.AddSubscription(
      this.form.controls['faultNotificationExecutorGroup'].valueChanges.subscribe(() => {
        this.syncFaultNotificationExecutorWebUsers(true);
      })
    );

    this.loadPropertyStatistics();
  }

  private loadPropertyStatistics(): void {
    // Fixes the issue that only one of the charts is updated.
    // TODO: check if there's any better fix.
    this.AddSubscription(
      forkJoin({
        dwellingStatistics: this.dwellingService.getPropertyStatistics(this.dataContext.id),
        otherRentalObjectStatistics: this.otherRenalObjectService.getPropertyStatistics(this.dataContext.id),
        parkingStatistics: this.parkingService.getPropertyStatistics(this.dataContext.id),
        premisesStatistics: this.premisesService.getPropertyStatistics(this.dataContext.id),
      }).subscribe({
        next: data => {
          var dwellingStatistics = data.dwellingStatistics;
          dwellingStatistics.category = RentalObjectCategory.Dwelling;
          this.dwelling = new ResidentialProperty(dwellingStatistics);
          this.dwellingExist = this.dwelling.totalRentalObjectCount > 0;

          var otherRentalObjectStatistics = data.otherRentalObjectStatistics;
          otherRentalObjectStatistics.category = RentalObjectCategory.Other;
          this.otherRentalObject = new NonResidentialProperty(otherRentalObjectStatistics);
          this.otherRentalObjectExist = this.otherRentalObject.totalRentalObjectCount > 0;

          var parkingStatistics = data.parkingStatistics;
          parkingStatistics.category = RentalObjectCategory.Parking;
          this.parking = new NonResidentialProperty(parkingStatistics);
          this.parkingExist = this.parking.totalRentalObjectCount > 0;

          var premisesStatistics = data.premisesStatistics;
          premisesStatistics.category = RentalObjectCategory.Premises;
          this.premises = new ResidentialProperty(premisesStatistics);
          this.premisesExist = this.premises.totalRentalObjectCount > 0;
        },
        error: error => this.messageService.showError(error),
      })
    );
  }

  updatePriceListSelection(): void {
    if (this.form.controls['company'].value && this._imdPriceLists && this._companies) {
      let company = this._companies.find(c => c.id === this.form.controls['company'].value);
      if (company) {
        this.imdColdWaterSelection = this._imdPriceLists
          .filter(l => l.currencyCode === company.currencyCode && l.meterType === MeterMediaType.ColdWater)
          .map(l => new SelectionItem(l.name, l.id));
        this.imdColdWaterSelection.splice(0, 0, new SelectionItem('COMMON.NOT_CHARGED', null));

        this.imdElectricitySelection = this._imdPriceLists
          .filter(l => l.currencyCode === company.currencyCode && l.meterType === MeterMediaType.Electricity)
          .map(l => new SelectionItem(l.name, l.id));
        this.imdElectricitySelection.splice(0, 0, new SelectionItem('COMMON.NOT_CHARGED', null));

        this.imdChargingElectricitySelection = this._imdPriceLists
          .filter(l => l.currencyCode === company.currencyCode && l.meterType === MeterMediaType.ChargingElectricity)
          .map(l => new SelectionItem(l.name, l.id));
        this.imdChargingElectricitySelection.splice(0, 0, new SelectionItem('COMMON.NOT_CHARGED', null));

        this.imdHotWaterSelection = this._imdPriceLists
          .filter(l => l.currencyCode === company.currencyCode && l.meterType === MeterMediaType.WarmWater)
          .map(l => new SelectionItem(l.name, l.id));
        this.imdHotWaterSelection.splice(0, 0, new SelectionItem('COMMON.NOT_CHARGED', null));

        this.imdHeatingSelection = this._imdPriceLists
          .filter(l => l.currencyCode === company.currencyCode && l.meterType === MeterMediaType.Heating)
          .map(l => new SelectionItem(l.name, l.id));
        this.imdHeatingSelection.splice(0, 0, new SelectionItem('COMMON.NOT_CHARGED', null));

        if (
          this.form.controls['imdPriceListColdWaterId'].value &&
          !this.imdColdWaterSelection.find(l => l.value === this.form.controls['imdPriceListColdWaterId'].value)
        ) {
          this.form.controls['imdPriceListColdWaterId'].setValue(null);
        }
        if (
          this.form.controls['imdPriceListElectricityId'].value &&
          !this.imdElectricitySelection.find(l => l.value === this.form.controls['imdPriceListElectricityId'].value)
        ) {
          this.form.controls['imdPriceListElectricityId'].setValue(null);
        }
        if (
          this.form.controls['imdPriceListChargingElectricityId'].value &&
          !this.imdChargingElectricitySelection.find(l => l.value === this.form.controls['imdPriceListChargingElectricityId'].value)
        ) {
          this.form.controls['imdPriceListChargingElectricityId'].setValue(null);
        }
        if (
          this.form.controls['imdPriceListHotWaterId'].value &&
          !this.imdHotWaterSelection.find(l => l.value === this.form.controls['imdPriceListHotWaterId'].value)
        ) {
          this.form.controls['imdPriceListHotWaterId'].setValue(null);
        }
        if (
          this.form.controls['imdPriceListHeatingId'].value &&
          !this.imdHeatingSelection.find(l => l.value === this.form.controls['imdPriceListHeatingId'].value)
        ) {
          this.form.controls['imdPriceListHeatingId'].setValue(null);
        }
      }
    }
  }

  documentsLoaded: boolean;
  setDocumentsVisibility(show: boolean) {
    if (this.documentsLoaded) {
      return;
    }
    this.AddSubscription(
      this.documentService.getByOwnerId(this.dataContext.id).subscribe(x => {
        this.dataContext.documents = x;
        this.documentsLoaded = true;
      })
    );
  }

  linkDocument() {
    this.AddSubscription(
      this._actionWindowService
        .open(PropertyDocumentFormComponent, this._injector, null, this.dataContext)
        .pipe(switchMap(x => this.documentService.getByOwnerId(this.dataContext.id)))
        .subscribe(res => (this.dataContext.documents = res))
    );
  }

  onPropertyTaxesChange(payload: {
    drafts: PropertyTax[];
    removedPropertyTaxIds: string[];
    removedBuildingPropertyTaxAssessmentIds: string[];
    hasChanges: boolean;
  }): void {
    this.pendingPropertyTaxes = payload?.drafts || [];
    this.pendingRemovedPropertyTaxIds = payload?.removedPropertyTaxIds || [];
    this.pendingRemovedBuildingPropertyTaxAssessmentIds = payload?.removedBuildingPropertyTaxAssessmentIds || [];
    if (payload?.hasChanges) {
      this.form?.markAsDirty();
    }
  }

  protected prepareSave(): Property {
    const model = super.prepareSave();
    model.hasNegotiationProcedure = this.form.controls['negotiatingClause'].value;
    model.faultNotificationRecipientId = this.form.controls['faultNotificationRecipient'].value?.id;
    model.faultNotificationExecutorWebUserId = this.form.controls['faultNotificationExecutorWebUser'].value?.id;
    model.dwellingInspectionExecutorWebUserId = this.form.controls['dwellingInspectionExecutorWebUser'].value?.id;
    model.faultNotificationExecutorGroupId = this.form.controls['faultNotificationExecutorGroup'].value?.id;

    model.propertyTaxes = this.pendingPropertyTaxes;
    model.removedPropertyTaxIds = this.pendingRemovedPropertyTaxIds;
    model.removedBuildingPropertyTaxAssessmentIds = this.pendingRemovedBuildingPropertyTaxAssessmentIds;

    return model;
  }

  override onSubmit(): void {
    if (this.errandSubCategoryResponsibilities && !this.errandSubCategoryResponsibilities.validate()) {
      return;
    }

    super.onSubmit();
  }

  override innerFormDirty(): boolean {
    return !!this.errandSubCategoryResponsibilities?.dirty;
  }

  protected override formSubmittedChange(response?: any): void {
    const saveResponsibilities = this.errandSubCategoryResponsibilities?.dirty
      ? this.errandSubCategoryResponsibilities.saveChanges()
      : of(null);

    this.AddSubscription(
      saveResponsibilities.subscribe({
        next: () => {
          super.formSubmittedChange(response);
          this.navigation.reloadCurrentRoute();
        },
        error: error => {
          this.formSubmittedWithError();
          this.messageService.showError(error);
        },
      })
    );
  }

  private applyDimensionLabels(labels: DimensionLabels) {
    this._dimensionLabelsService.setControlCaption(this.form?.controls['costPlace'], labels.costPlaceLabel);
    this._dimensionLabelsService.setControlCaption(this.form?.controls['costBearer'], labels.costBearerLabel);
    this._dimensionLabelsService.setControlHelpText(this.form?.controls['costPlace'], this.getPropertyCostPlaceHelpText(labels));
    this._dimensionLabelsService.setControlHelpText(this.form?.controls['costBearer'], this.getPropertyCostBearerHelpText(labels));
  }

  private getPropertyCostPlaceHelpText(labels: DimensionLabels): string {
    return this._languageService.getText('PROPERTY.HELP_TEXT.COST_PLACE', { dimension: labels.costPlaceLabel });
  }

  private getPropertyCostBearerHelpText(labels: DimensionLabels): string {
    return this._languageService.getText('PROPERTY.HELP_TEXT.COST_BEARER', { dimension: labels.costBearerLabel });
  }

  private syncFaultNotificationExecutorWebUsers(resetInvalidSelection: boolean = false): void {
    const executorWebUserControl = this.form?.controls['faultNotificationExecutorWebUser'];
    const executorGroupControl = this.form?.controls['faultNotificationExecutorGroup'];

    if (!executorWebUserControl || !executorGroupControl) {
      return;
    }

    if (
      resetInvalidSelection &&
      !isExecutorUserInGroup(executorWebUserControl.value, executorGroupControl.value, this.webUserExecutorGroups)
    ) {
      executorWebUserControl.setValue(null);
    }

    this.faultNotificationExecutorWebUsers = filterExecutorOptions(
      this.executorWebUsers,
      executorGroupControl.value,
      this.webUserExecutorGroups,
      item => item.id,
      executorWebUserControl.value
    );
  }
}
